import { useCallback } from 'react';
import useHttp from './useHttp';
import { useAuth } from '../context/authContext';

const useAgent = () => {
  const { getAuthHeaders } = useAuth();
  const { isLoading, error, sendRequest } = useHttp();

  const buildHeaders = useCallback(() => {
    const headers = {
      ...getAuthHeaders({ withAuth: true }),
    };
    if (import.meta.env.VITE_SERVICE_API_KEY) {
      headers['X-API-Key'] = import.meta.env.VITE_SERVICE_API_KEY;
    }
    return headers;
  }, [getAuthHeaders]);

  const fetchSessions = useCallback(async () => {
    return await sendRequest({
      url: '/api/v1/agent/session?limit=5',
      method: 'GET',
      headers: buildHeaders(),
      absolute: true,
    });
  }, [sendRequest, buildHeaders]);

  const loadSessionMessages = useCallback(
    async token => {
      return await sendRequest({
        url: `/api/v1/agent/session/${token}`,
        method: 'GET',
        headers: buildHeaders(),
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const loadAuditSessionMessages = useCallback(
    async token => {
      return await sendRequest({
        url: `/api/v1/agent/session/audit/${token}`,
        method: 'GET',
        headers: buildHeaders(),
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const createSession = useCallback(async () => {
    return await sendRequest({
      url: '/api/v1/agent/session',
      method: 'POST',
      headers: buildHeaders(),
      absolute: true,
    });
  }, [sendRequest, buildHeaders]);

  const sendMessage = useCallback(
    async (sessionToken, message, domain) => {
      return await sendRequest({
        url: '/api/v1/agent/message',
        method: 'POST',
        headers: buildHeaders(),
        body: { sessionToken, message, domain },
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const sendHumanMessage = useCallback(
    async (sessionToken, message) => {
      return await sendRequest({
        url: '/api/v1/agent/human-message',
        method: 'POST',
        headers: buildHeaders(),
        body: { sessionToken, message },
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const claimEscalatedSessionLock = useCallback(
    async sessionToken => {
      return await sendRequest({
        url: `/api/v1/agent/session/${sessionToken}/lock`,
        method: 'POST',
        headers: buildHeaders(),
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const releaseEscalatedSessionLock = useCallback(
    async sessionToken => {
      return await sendRequest({
        url: `/api/v1/agent/session/${sessionToken}/lock`,
        method: 'DELETE',
        headers: buildHeaders(),
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const fetchEscalatedSessions = useCallback(async () => {
    return await sendRequest({
      url: '/api/v1/agent/escalated',
      method: 'GET',
      headers: buildHeaders(),
      absolute: true,
    });
  }, [sendRequest, buildHeaders]);

  const fetchEscalatedCounts = useCallback(async () => {
    return await sendRequest({
      url: '/api/v1/agent/escalated/counts',
      method: 'GET',
      headers: buildHeaders(),
      absolute: true,
    });
  }, [sendRequest, buildHeaders]);

  const getSessionStats = useCallback(
    async (from, to) => {
      return await sendRequest({
        url: '/api/v1/agent/session/stats',
        method: 'GET',
        headers: buildHeaders(),
        params: { from, to },
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const fetchRatedSessions = useCallback(
    async ({ from, to, helpful, limit = 50, skip = 0 }) => {
      const params = { from, to, limit, skip };
      if (typeof helpful === 'boolean') {
        params.helpful = helpful ? 'true' : 'false';
      }
      return await sendRequest({
        url: '/api/v1/agent/session/rated',
        method: 'GET',
        headers: buildHeaders(),
        params,
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const fetchAuditSessions = useCallback(
    async ({ from, to, status, type, q, hasRating, hasCandidate, limit = 50, skip = 0 } = {}) => {
      return await sendRequest({
        url: '/api/v1/agent/session/audit',
        method: 'GET',
        headers: buildHeaders(),
        params: { from, to, status, type, q, hasRating, hasCandidate, limit, skip },
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const ratePublicSession = useCallback(
    async (sessionToken, { helpful, comment } = {}) => {
      if (!sessionToken) return null;
      return await sendRequest({
        url: `/api/v1/agent/public/session/${sessionToken}/rating`,
        method: 'POST',
        headers: buildHeaders(),
        body: { helpful, comment },
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  const searchCandidates = useCallback(
    async payload => {
      return await sendRequest({
        url: '/api/v1/agent/candidates/search',
        method: 'POST',
        headers: buildHeaders(),
        body: { ...payload },
        absolute: true,
      });
    },
    [sendRequest, buildHeaders]
  );

  return {
    isLoading,
    error,
    fetchSessions,
    loadSessionMessages,
    loadAuditSessionMessages,
    createSession,
    sendMessage,
    sendHumanMessage,
    claimEscalatedSessionLock,
    releaseEscalatedSessionLock,
    fetchEscalatedSessions,
    fetchEscalatedCounts,
    getSessionStats,
    fetchRatedSessions,
    fetchAuditSessions,
    ratePublicSession,
    searchCandidates,
  };
};

export default useAgent;
